/**
 * Shared scaffold for the organization list screens.
 *
 * Companies, branches, departments and locations are the same screen with
 * different columns and a different create form: a list, a create panel, and
 * the three states every list needs (loading, failed, empty). Writing that
 * four times invites the four copies to drift, and the empty and error states
 * are exactly what gets skipped when they do.
 *
 * The parent picker is a first-class part of it. Each of these rows is
 * meaningless without its parent — a branch with no company cannot be posted
 * to — so the scaffold refuses to render a create form when no parent exists
 * and explains what to create first, rather than offering an empty <select>
 * that produces a 422 on submit.
 *
 * Arabic and English (C7): its own words (Name, Code, Cancel, Save, the
 * failed load) come from the catalogs; the title, columns and fields a page
 * passes in are that page's to translate. An API refusal is shown through
 * ErrorText, so it is in the interface's language.
 */
import { useState, type ReactNode } from "react";
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { post, useApiList, useApiMutation } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";

export interface ParentOption {
  id: string;
  label: string;
}

export interface OrgListPageProps<TRow extends object> {
  title: string;
  subtitle: string;
  breadcrumb: string[];
  /** Canonical API path, e.g. `/branches`. */
  path: string;
  /** Query key segment, e.g. `branches`. */
  queryKey: string;
  columns: Array<{
    key: string;
    label: string;
    numeric?: boolean;
    render?: (row: TRow) => ReactNode;
  }>;
  emptyTitle: string;
  emptyMessage: string;
  createLabel: string;
  /** The required parent for a new row; omitted when the entity has none. */
  parent?: {
    label: string;
    field: string;
    options: ParentOption[];
    isLoading: boolean;
    /** Shown instead of the form when no parent exists yet. */
    missingMessage: string;
  };
  /** Extra fields appended to the name/code pair. */
  extraFields?: Array<{
    field: string;
    label: string;
    hint?: string;
    placeholder?: string;
  }>;
}

export function OrgListPage<TRow extends object>({
  title,
  subtitle,
  breadcrumb,
  path,
  queryKey,
  columns,
  emptyTitle,
  emptyMessage,
  createLabel,
  parent,
  extraFields = [],
}: OrgListPageProps<TRow>) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useApiList<TRow>(["org", queryKey], path);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});

  const create = useApiMutation<Record<string, string>, unknown>(
    ["org", queryKey],
    (input) => {
      const body: Record<string, unknown> = {
        name: input.name,
        code: input.code,
      };
      if (parent) body[parent.field] = input[parent.field];
      for (const extra of extraFields) {
        if (input[extra.field]) body[extra.field] = input[extra.field];
      }
      return post(path, body);
    },
  );

  const parentMissing = parent
    ? !parent.isLoading && parent.options.length === 0
    : false;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    create.mutate(form, {
      onSuccess: () => {
        setCreating(false);
        setForm({});
      },
    });
  };

  const openCreate = () => {
    const firstParent = parent?.options[0];
    setForm(parent && firstParent ? { [parent.field]: firstParent.id } : {});
    setCreating(true);
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumb={breadcrumb}
        actions={
          <Button
            variant="primary"
            disabled={parentMissing}
            onClick={() => (creating ? setCreating(false) : openCreate())}
          >
            {creating ? t("common.cancel") : createLabel}
          </Button>
        }
      />

      {parentMissing && (
        <Card className="mb-4">
          <p style={{ margin: 0 }}>{parent?.missingMessage}</p>
        </Card>
      )}

      {creating && !parentMissing && (
        <Card title={createLabel} className="mb-4">
          <form onSubmit={submit}>
            <div className="atlas-grid atlas-grid--2">
              {parent && (
                <Field label={parent.label} required>
                  <Select
                    value={form[parent.field] ?? ""}
                    required
                    onChange={(e) =>
                      setForm({ ...form, [parent.field]: e.target.value })
                    }
                  >
                    {parent.options.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <Field label={t("fields.name")} required>
                <Input
                  value={form.name ?? ""}
                  required
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
              <Field
                label={t("fields.code")}
                required
                hint={t("org.list.codeHint")}
              >
                <Input
                  value={form.code ?? ""}
                  required
                  onChange={(e) =>
                    setForm({ ...form, code: e.target.value.toUpperCase() })
                  }
                />
              </Field>
              {extraFields.map((extra) => (
                <Field key={extra.field} label={extra.label} hint={extra.hint}>
                  <Input
                    value={form[extra.field] ?? ""}
                    placeholder={extra.placeholder}
                    onChange={(e) =>
                      setForm({ ...form, [extra.field]: e.target.value })
                    }
                  />
                </Field>
              ))}
            </div>
            {create.error && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlock: 8,
                }}
              >
                <ErrorText error={create.error} />
              </div>
            )}
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </form>
        </Card>
      )}

      {isLoading ? (
        <Skeleton rows={8} />
      ) : error ? (
        // The reason goes where EmptyState puts its action: it can carry a
        // Details disclosure, which a <p> message cannot hold.
        <EmptyState
          title={t("resource.loadFailed", { title })}
          action={
            <div role="alert" style={{ fontSize: 14 }}>
              <ErrorText error={error} />
            </div>
          }
        />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          message={emptyMessage}
          action={
            parentMissing ? undefined : (
              <Button variant="primary" onClick={openCreate}>
                {createLabel}
              </Button>
            )
          }
        />
      ) : (
        <Card title={listTitle(title, data.total)}>
          <DataTable<TRow> rows={data.items} columns={columns} />
        </Card>
      )}
    </div>
  );
}
